import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Lane, LaneDirection, LaneInput } from '@kinetic-ui/data-layer';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLaneForm } from './useLaneForm';
import useLaneStyles from './styles';
import { isEqual } from 'lodash';
import { LocationFormContext } from '../../context';
import { nanoid } from 'nanoid';
import { Alert } from '@mui/material';
import { useSelectedLane } from './selectedLaneState';
import { LANES_FORM_ID } from '../../const';
import { TextField } from '@qfa/forms';
import { RecursivePartial } from 'types';
import { usePrevious, useRouteBuilder } from 'kineticUtils';
import { LOCATIONS_MODULE_ROUTE } from 'const';
import { KineticForm, renderView } from 'components/forms';
import Dialog from 'components/Dialog';

const newLaneValues: Partial<LaneInput> = {
  id: '',
  description: '',
  geometry: {
    type: 'LineString',
    coordinates: [],
  },
};

type LaneFormProps = {
  currentLane?: RecursivePartial<LaneInput>;
  isNew?: boolean;
  onClose?: () => void;
};

export function LaneForm({ isNew, currentLane, onClose }: LaneFormProps) {
  const { t } = useTranslation(['locations', 'common']);
  const classes = useLaneStyles();
  const route = useRouteBuilder(LOCATIONS_MODULE_ROUTE);
  const { setValues: setLocationValues, setDirty } = useContext(LocationFormContext);
  const { selectedLane, setSelectedLane } = useSelectedLane();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [directionChangeConfirmOpen, setDirectionChangeConfirmOpen] = useState<boolean>(false);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
  const { values: locationFormValues, disabled } = useContext(LocationFormContext);

  const initalData = selectedLane
    ? {
        ...newLaneValues,
        id: selectedLane.id,
        description: selectedLane.description,
        laneDirection: selectedLane.laneDirection,
        travelDirection: selectedLane.travelDirection,
        geometry: selectedLane.geometry,
      }
    : newLaneValues;

  const { errors, formProps, values, isSaveDisabled, viewProps, setDirtyFields } = useLaneForm(
    initalData,
    isNew,
    disabled
  );

  const [formValues, setValues, reset] = values;
  const { hasErrors } = errors;

  const previousDirection = usePrevious(formValues.laneDirection);
  const canChangeDirections = useMemo(() => {
    if (!previousDirection || previousDirection === formValues.laneDirection) {
      return true;
    }
    if (
      locationFormValues.maneuvers?.some(
        (maneuver) =>
          (formValues.laneDirection === LaneDirection.Approach
            ? maneuver.refDepartureLane
            : maneuver.refApproachLane) === selectedLane?.id
      )
    ) {
      return false;
    }
    return true;
  }, [formValues.laneDirection, locationFormValues.maneuvers, selectedLane?.id, previousDirection]);

  useEffect(() => {
    if (!canChangeDirections) {
      setDirectionChangeConfirmOpen(true);
    } else {
      setSelectedLane((prev) => ({ ...prev, laneDirection: formValues.laneDirection }));
    }
  }, [canChangeDirections, formValues.laneDirection, setSelectedLane]);

  useEffect(() => {
    if (currentLane && isEqual(currentLane.geometry, selectedLane?.geometry)) {
      return;
    }
    setValues((prev) => ({ ...prev, geometry: selectedLane?.geometry }));
    setDirtyFields((prev) => [...prev, 'geometry']);
  }, [currentLane, selectedLane?.geometry, setDirtyFields, setValues]);

  const handleUpdate = (lane: Lane) => {
    setDirty(LANES_FORM_ID, true);
    setLocationValues((prev) => {
      const index = prev?.lanes?.findIndex((search) => search.id === lane.id) ?? 0;
      const newLanes = prev?.lanes?.filter((search) => search.id !== lane.id) ?? [];
      newLanes.splice(index, 0, lane);
      return {
        ...prev,
        lanes: newLanes,
      };
    });
    setSelectedLane((prev) => ({ ...prev, ...lane }));
    onClose?.();
  };

  const handleAdd = (lane: Lane) => {
    setDirty(LANES_FORM_ID, true);
    setLocationValues((prev) => {
      return {
        ...prev,
        lanes: [...(prev.lanes ?? []), lane],
      };
    });
    onClose?.();
  };

  const handleRemove = (id: string) => {
    setDirty(LANES_FORM_ID, true);
    setLocationValues((prev) => {
      return {
        ...prev,
        lanes: prev?.lanes?.filter((search) => search.id !== id) ?? [],
        // update maneuvers to remove any associations with the deleted lane
        maneuvers: prev.maneuvers?.filter(
          (m) => m.refApproachLane !== id && m.refDepartureLane !== id
        ),
      };
    });
    setSelectedLane(undefined);
    onClose?.();
  };

  const onCancel = () => {
    setSelectedLane(undefined);
    onClose?.();
  };

  const handleSave = () => {
    if (hasErrors) {
      return;
    }
    // if there are no errors the formValues with contain all the correct values.
    const toSave = {
      ...selectedLane,
      ...formValues,
      geometry: selectedLane?.geometry,
    } as Lane;

    if (isNew) {
      toSave.id = nanoid();
      handleAdd(toSave);
    } else {
      handleUpdate(toSave);
    }

    reset(toSave);
  };

  return (
    <div>
      {isNew ? (
        <Alert severity='info' sx={{ mt: 2 }}>
          {t('newLaneHelp')}
        </Alert>
      ) : null}

      <KineticForm {...formProps} flexDirection='column'>
        {({ fieldComponents }) => renderView(viewProps, fieldComponents, route)}
      </KineticForm>

      <div className={classes.actionButtons}>
        <div>
          {!isNew && (
            <IconButton disabled={disabled} onClick={() => setDeleteConfirmOpen(true)}>
              <DeleteIcon />
            </IconButton>
          )}
        </div>
        <div>
          <Button
            disabled={disabled}
            className={classes.actionButton}
            onClick={onCancel}
            color='secondary'
          >
            {t('common:cancel')}
          </Button>
          <Button
            className={classes.actionButton}
            disabled={isSaveDisabled || disabled}
            onClick={handleSave}
            variant='contained'
          >
            {isNew ? t('common:create') : t('common:update')}
          </Button>
        </div>
      </div>

      <Dialog
        open={directionChangeConfirmOpen}
        title=''
        buttons={
          <Button
            onClick={() => {
              setDirectionChangeConfirmOpen(false);
              setValues((prev: Partial<LaneInput>) => ({
                ...prev,
                laneDirection:
                  formValues.laneDirection === LaneDirection.Approach
                    ? LaneDirection.Departure
                    : LaneDirection.Approach,
              }));
            }}
          >
            {t('common:ok')}
          </Button>
        }
      >
        <Typography>
          {t('laneDirectionChangeWarning', { lane: selectedLane?.description ?? '' })}
        </Typography>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        title={`${t('laneDeleteConfirm')}`}
        buttons={
          <>
            <Button
              onClick={() => {
                setDeleteConfirmValue('');
                setDeleteConfirmOpen(false);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                setDeleteConfirmOpen(false);
                handleRemove(selectedLane?.id as string);
                setDeleteConfirmValue('');
              }}
              disabled={deleteConfirmValue !== 'CONFIRM DELETE'}
              variant='contained'
              className={classes.warningButton}
            >
              {t('deleteLane')}
            </Button>
          </>
        }
      >
        <Typography component='div'>
          <Alert severity='warning'>
            <p style={{ marginTop: 0 }}>
              <strong>{t('laneDeleteConfirmPrompt1')}</strong>
            </p>
            <p>{t('laneDeleteConfirmPrompt2')}</p>
            <p>{t('common:cantUndo')}</p>
            <p>{t('laneDeleteConfirmPrompt3')}</p>
          </Alert>
          <TextField
            id='deleteConfirm'
            label={t('confirmFieldLabel')}
            name='deleteConfirm'
            type='text'
            value={deleteConfirmValue}
            autoComplete='off'
            onChange={(_, value) => setDeleteConfirmValue(value)}
          />
        </Typography>
      </Dialog>
    </div>
  );
}
